/**
 * DailyInspirationReassignModal — picker shown when an admin clicks an
 * empty day on the Daily Inspirations calendar. Per design intent the
 * empty-cell flow doesn't create new content; it lets the admin pick
 * an existing inspiration and reschedule it onto the empty day. The
 * "+ New Inspiration" button in the PageHeader is still the create
 * affordance.
 *
 * Behaviour:
 *   • Lists every existing inspiration (the same array the calendar
 *     reads from), searchable by title or id.
 *   • Picking one fires either:
 *       — Empty target slot:
 *           GET  /daily-inspirations/{id}    (read full payload — list
 *                                             endpoint may be lean)
 *           PUT  /daily-inspirations/{id}    (echo payload + new slot)
 *       — Occupied target slot (the common case in the steady state,
 *         since perpetual scheduling fills all 365 slots):
 *           POST /daily-inspirations/swap    (atomic transaction —
 *                                             two PUTs would collide
 *                                             on the UNIQUE schedule
 *                                             index)
 *     then re-fetches the list so the calendar reflects the move.
 *   • The source day (previous date of the picked inspiration) becomes
 *     empty automatically in the move path; in the swap path it gets
 *     the previous occupant.
 *
 * The selection list re-uses the date format from the list view
 * ("Sep 14, 2026 · Sat") so the user can eyeball where each
 * inspiration currently lives before reassigning it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { parseISO, set, getUnixTime, format } from 'date-fns';
import { apiV2 } from '../../../../actions';
import { fetchAllDailyReflections } from '../../../../actions/dailyReflectionActions';
import { Modal, Button, cn } from '../../../redesign';
import SeasonalChip from './SeasonalChip';

/* Date keys the picked inspiration is rescheduled TO are passed in as
 * `targetDateKey` ("YYYY-MM-DD"). We compute the UNIX-seconds epoch
 * the PUT expects by anchoring to midday on that day (12:00 local) so
 * minor timezone wiggles can't slip the row into the wrong calendar
 * cell. (`date_epoch` is interpreted as seconds since 1970.) */
function epochSecondsForDateKey(dateKey) {
  const day = parseISO(dateKey);
  const noon = set(day, { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 });
  return getUnixTime(noon);
}

/* Derive (month, day) from a "YYYY-MM-DD" key. APIv2 PR #583 made
 * (schedule_month, schedule_day) the source of truth for the
 * perpetual yearly schedule; date_epoch is sent alongside as a
 * transition crutch. */
function monthDayForDateKey(dateKey) {
  const day = parseISO(dateKey);
  return { schedule_month: day.getMonth() + 1, schedule_day: day.getDate() };
}

/* Build a payload to PUT a DI to a specific (month, day) slot, echoing
 * its existing payload values so the API gets a complete record. */
function buildReschedulePayload(full, dateKey) {
  const { schedule_month, schedule_day } = monthDayForDateKey(dateKey);
  return {
    title: full.title,
    day: full.day,
    date_epoch: epochSecondsForDateKey(dateKey),
    schedule_month,
    schedule_day,
    is_seasonal: full.is_seasonal ? 1 : 0,
    is_premium: full.is_premium,
    audio_length: full.audio_length,
    audio_url: full.audio_url,
    image_url: full.image_url,
    author_id: full.author_id,
    deeplink_url: full.deeplink_url,
    deeplink_image_url: full.deeplink_image_url,
  };
}

export default function DailyInspirationReassignModal({
  open,
  onClose,
  targetDateKey,
  inspirations,
}) {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  /* Clear stale state every time the modal opens or the target slot
   * changes. Without this, an error set on one open (e.g. tapping
   * the same-DI guard below, or a failed PUT/swap) survives across
   * the close→reopen cycle because the component stays mounted in
   * the parent tree. Caught by Cursor Bugbot on PR #410. */
  useEffect(() => {
    if (open) {
      setError(null);
      setBusyId(null);
      setQuery('');
    }
  }, [open, targetDateKey]);

  /* Sort by (schedule_month, schedule_day) ascending — calendar order.
   * Unscheduled rows (NULL schedule columns) drop to the top, since
   * they're the natural candidates for assignment. Fallback to
   * date_epoch when the schedule columns aren't populated yet (i.e.
   * pre-APIv2-#583 servers — defensive only). */
  const sorted = useMemo(() => {
    if (!Array.isArray(inspirations)) return [];
    const slot = (it) => {
      const m = Number(it.schedule_month);
      const d = Number(it.schedule_day);
      if (!m || !d) return -1; // bubble unscheduled rows to the top
      return m * 100 + d; // 0501..1231, comparable as an int
    };
    return [...inspirations].sort((a, b) => slot(a) - slot(b));
  }, [inspirations]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((it) => {
      const inTitle = (it.title || '').toLowerCase().includes(q);
      const inId    = String(it.daily_reflection_id).includes(q);
      return inTitle || inId;
    });
  }, [sorted, query]);

  const targetDateLabel = targetDateKey
    ? format(parseISO(targetDateKey), 'EEE, MMM d, yyyy')
    : '';

  /* Detect whether the target slot is occupied by another DI. With
   * perpetual scheduling and the unique (schedule_month, schedule_day)
   * constraint, all 365 calendar slots are typically filled — so
   * picking an existing DI to put on `targetDateKey` will almost
   * always need a SWAP rather than a one-way move (Monday 12107892350,
   * Kelly's note: "the swap would happen in the front end, it would
   * just swap one day with another"). */
  const targetOccupant = useMemo(() => {
    if (!targetDateKey) return null;
    const { schedule_month, schedule_day } = monthDayForDateKey(targetDateKey);
    return (
      inspirations?.find(
        (it) =>
          Number(it.schedule_month) === schedule_month
          && Number(it.schedule_day) === schedule_day,
      ) ?? null
    );
  }, [targetDateKey, inspirations]);

  /* Move/swap handler.
   *
   * Move path (empty target slot): GET the picked DI to read its full
   * payload (the list endpoint is lean — title / id / date), then PUT
   * it back with the new (schedule_month, schedule_day).
   *
   * Swap path (target slot is occupied): POST a single atomic call to
   * /daily-inspirations/swap. The shared UNIQUE (schedule_month,
   * schedule_day) index makes a two-PUT swap impossible — the first
   * PUT collides with the row about to vacate — so the backend does
   * the shuffle in a DB transaction (APIv2 PR #583). */
  /* Feb 29 is reserved for the leap-year evergreen-fallback pick on
   * the server — no DI is ever permanently scheduled there. The
   * calendar cell already renders Feb 29 as informational and
   * non-clickable, but we keep this guard as a belt-and-suspenders
   * in case anything else routes a targetDateKey here (deep link,
   * keyboard nav, programmatic open). monthDayForDateKey returns
   * (schedule_month, schedule_day) — month is 1-based here. */
  const isFeb29Target = useMemo(() => {
    if (!targetDateKey) return false;
    const { schedule_month, schedule_day } = monthDayForDateKey(targetDateKey);
    return schedule_month === 2 && schedule_day === 29;
  }, [targetDateKey]);

  const handlePick = async (item) => {
    if (busyId) return;
    if (isFeb29Target) {
      setError(
        'Feb 29 is picked automatically from the non-seasonal pool on leap years. Pick another date.',
      );
      return;
    }
    if (targetOccupant && targetOccupant.daily_reflection_id === item.daily_reflection_id) {
      setError('That DI is already scheduled on this day.');
      return;
    }
    setBusyId(item.daily_reflection_id);
    setError(null);
    try {
      if (!targetOccupant) {
        // Plain move into an empty slot (e.g. an orphan with NULL
        // schedule columns). PUT needs the full record.
        const { data: pickedFull } = await apiV2.get(
          `/daily-inspirations/${item.daily_reflection_id}`,
        );
        await apiV2.put(
          `/daily-inspirations/${item.daily_reflection_id}`,
          buildReschedulePayload(pickedFull, targetDateKey),
        );
      } else {
        // Swap is one atomic call — the server runs the 3-step
        // shuffle (A→NULL, B→A's slot, A→B's slot) inside a DB
        // transaction so the unique (month, day) index never sees a
        // mid-swap collision.
        await apiV2.post('/daily-inspirations/swap', {
          daily_reflection_id_a: item.daily_reflection_id,
          daily_reflection_id_b: targetOccupant.daily_reflection_id,
        });
      }

      dispatch(fetchAllDailyReflections());
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to reschedule. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const modalTitle = targetDateLabel
    ? targetOccupant
      ? `Swap with ${targetDateLabel}`
      : `Reschedule to ${targetDateLabel}`
    : 'Reschedule inspiration';
  const footerHint = targetOccupant
    ? `${targetDateLabel} currently has "${targetOccupant.title}" — picking another DI swaps the two dates.`
    : 'Pick an existing inspiration to move it to this day.';

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="Daily Inspirations"
      title={modalTitle}
      footer={
        <>
          <span
            className="text-text-muted mr-auto"
            style={{ fontSize: 12 }}
          >
            {footerHint}
          </span>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </>
      }
    >
      <div className="flex flex-col" style={{ gap: 10 }}>
        {/* Search box — title + id substring match. */}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by title or #id…"
          className="w-full bg-cream border border-sand text-navy focus:bg-surface focus:border-teal-green"
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: 13.5,
            outline: 'none',
            fontFamily: 'inherit',
          }}
          onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px #75A29318'; }}
          onBlur={(e)  => { e.currentTarget.style.boxShadow = 'none'; }}
        />

        {error && (
          <div
            className="text-coral"
            style={{
              fontSize: 12.5,
              padding: '8px 10px',
              borderRadius: 6,
              background: '#E58A6A14',
              border: '1px solid #E58A6A44',
            }}
          >
            {error}
          </div>
        )}

        {/* Scrollable picker list — capped at ~360px so the modal stays
         *  on screen even with 365 rows in the dataset. */}
        <div
          className="border border-sand bg-surface"
          style={{
            borderRadius: 8,
            maxHeight: 360,
            overflow: 'auto',
          }}
        >
          {filtered.length === 0 ? (
            <div
              className="text-text-muted italic text-center"
              style={{ padding: '24px 12px', fontSize: 13 }}
            >
              No inspirations match "{query}".
            </div>
          ) : (
            filtered.map((it) => {
              const epoch = Number(it.date_epoch);
              const date = Number.isFinite(epoch) && epoch > 0
                ? new Date(epoch * 1000)
                : null;
              const dateLabel = date ? format(date, 'MMM d, yyyy · EEE') : 'Unscheduled';
              const isBusy = busyId === it.daily_reflection_id;
              return (
                <button
                  key={it.daily_reflection_id}
                  type="button"
                  onClick={() => handlePick(it)}
                  disabled={isBusy || busyId != null}
                  className={cn(
                    'w-full flex items-center text-left border-b border-line last:border-b-0',
                    'hover:bg-cream disabled:opacity-50 disabled:cursor-wait',
                  )}
                  style={{
                    padding: '10px 14px',
                    gap: 12,
                    background: 'transparent',
                    cursor: 'pointer',
                    transition: 'background 120ms ease',
                  }}
                >
                  {/* Title + optional Seasonal chip. The chip lives OUTSIDE
                   * the truncated title span so it can't be eaten by the
                   * ellipsis on long titles — same flex+shrink-0 pattern
                   * the calendar list uses (Cursor Bugbot on PR #410). */}
                  <span
                    className="inline-flex items-center font-semibold text-navy flex-1"
                    style={{ fontSize: 13.5, minWidth: 0, gap: 8 }}
                  >
                    <span className="truncate">{it.title || 'Untitled'}</span>
                    {it.is_seasonal ? <SeasonalChip /> : null}
                  </span>
                  <span
                    className={cn(
                      'font-mono shrink-0',
                      date ? 'text-text-light' : 'text-text-muted italic',
                    )}
                    style={{ fontSize: 12 }}
                  >
                    {dateLabel}
                  </span>
                  <span
                    className="font-mono text-text-muted shrink-0"
                    style={{ fontSize: 11.5, minWidth: 44, textAlign: 'right' }}
                  >
                    #{it.daily_reflection_id}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <p
          className="text-text-light"
          style={{ fontSize: 12, lineHeight: 1.5, margin: 0 }}
        >
          {targetOccupant ? (
            <>
              Picking another inspiration swaps it with{' '}
              <strong className="text-text font-semibold">"{targetOccupant.title}"</strong>{' '}
              — the picked DI moves to{' '}
              <strong className="text-text font-semibold">{targetDateLabel}</strong>{' '}
              and the current occupant takes the picked DI's previous date.
            </>
          ) : (
            <>
              Reassigning an inspiration moves it from its current date to{' '}
              <strong className="text-text font-semibold">{targetDateLabel}</strong>.
              The previous date becomes empty.
            </>
          )}
        </p>
      </div>
    </Modal>
  );
}
